import { useCallback, useEffect, useState } from 'react';
import { formatDateTime, formatMoney } from '@/lib/credit';
import { downloadCSV } from '@/lib/csv';
import {
  getStoreTransferHistory,
  resolveCreditCustomerId,
  type StoreTransferMovement,
} from '@/lib/storeBalance';

interface Props {
  merchantId: number | null;
  /** Id du carnet de crédit si connu (évite une résolution). */
  creditCustomerId?: number | null;
  /** users.id du client — permet de résoudre le carnet si besoin. */
  clientUserId?: number | null;
  currency?: string;
  /** Changez cette valeur pour forcer un rafraîchissement. */
  refreshKey?: number;
  title?: string;
  className?: string;
}

type RangeKey = 0 | 7 | 30;

const RANGE_OPTIONS: { key: RangeKey; label: string }[] = [
  { key: 7, label: '7 jours' },
  { key: 30, label: '30 jours' },
  { key: 0, label: 'Tout' },
];

/**
 * StoreTransferHistory — historique dédié des transferts de solde magasin
 * d'un client : mouvements sortants (envoyés) et entrants (reçus).
 * Filtre par période (7 / 30 jours / tout) et export CSV.
 */
export default function StoreTransferHistory({
  merchantId,
  creditCustomerId = null,
  clientUserId = null,
  currency = 'MAD',
  refreshKey = 0,
  title = 'Historique des transferts de solde magasin',
  className = '',
}: Props) {
  const [movements, setMovements] = useState<StoreTransferMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [range, setRange] = useState<RangeKey>(30);

  const load = useCallback(async () => {
    if (merchantId == null) {
      setMovements([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFailed(false);
    try {
      let creditId = creditCustomerId;
      if (creditId == null && clientUserId != null) {
        creditId = await resolveCreditCustomerId(merchantId, clientUserId);
      }
      if (creditId == null) {
        setMovements([]);
        return;
      }
      const rows = await getStoreTransferHistory(merchantId, creditId, range === 0 ? 300 : 500, range);
      setMovements(rows);
    } catch {
      setFailed(true);
      setMovements([]);
    } finally {
      setLoading(false);
    }
  }, [merchantId, creditCustomerId, clientUserId, range]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const sent = movements
    .filter((m) => m.type === 'transfer_out')
    .reduce((s, m) => s + Number(m.amount || 0), 0);
  const received = movements
    .filter((m) => m.type === 'transfer_in')
    .reduce((s, m) => s + Number(m.amount || 0), 0);

  const exportCsv = () => {
    const headers = ['Date', 'Sens', 'Montant', 'Devise', 'Description'];
    const rows = movements.map((m) => [
      new Date(m.created_at).toLocaleString('fr-FR'),
      m.type === 'transfer_out' ? 'Envoyé' : 'Reçu',
      String(Number(m.amount || 0)),
      currency,
      m.description || (m.type === 'transfer_out' ? 'Solde magasin envoyé' : 'Solde magasin reçu'),
    ]);
    const suffix = range === 0 ? 'tout' : `${range}j`;
    downloadCSV(`transferts-solde-magasin-${suffix}.csv`, headers, rows);
  };

  const rangeLabel = RANGE_OPTIONS.find((o) => o.key === range)?.label || '';

  return (
    <div className={`bg-background-50 border border-background-200/70 rounded-lg overflow-hidden ${className}`}>
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-background-200/70">
        <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-exchange-funds-line"></i>
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-foreground-950 truncate">{title}</h3>
          <p className="text-[11px] text-foreground-500">
            Envoyé : <span className="font-medium text-secondary-700">{formatMoney(sent, currency)}</span>
            {' · '}Reçu : <span className="font-medium text-accent-700">{formatMoney(received, currency)}</span>
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={movements.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          title="Exporter en CSV"
        >
          <i className="ri-file-download-line"></i>
          <span className="hidden sm:inline">CSV</span>
        </button>
      </div>

      {/* Filtre par période */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-background-200/70 bg-background-100/40">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400 hidden sm:inline">
          Période
        </span>
        <div className="inline-flex items-center gap-1 bg-background-50 border border-background-200/70 rounded-full p-1 ml-auto">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setRange(opt.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                range === opt.key
                  ? 'bg-secondary-500 text-background-50'
                  : 'text-foreground-600 hover:bg-background-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
        </div>
      ) : failed ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <p className="text-sm text-foreground-500 mb-3">Impossible de charger l&apos;historique.</p>
          <button
            onClick={load}
            className="px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
          >
            Réessayer
          </button>
        </div>
      ) : movements.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center px-4">
          <i className="ri-exchange-funds-line text-2xl text-foreground-300 mb-2"></i>
          <p className="text-sm text-foreground-500">
            Aucun transfert de solde magasin {rangeLabel ? `sur les ${rangeLabel.toLowerCase()}` : ''} pour le moment.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-background-200/70">
          {movements.map((m) => {
            const isOut = m.type === 'transfer_out';
            return (
              <div key={m.id} className="flex items-center gap-3 px-4 py-3">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isOut ? 'bg-secondary-100 text-secondary-700' : 'bg-accent-100 text-accent-700'
                  }`}
                >
                  <i className={isOut ? 'ri-arrow-right-up-line' : 'ri-arrow-left-down-line'}></i>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground-900 truncate">
                    {isOut ? 'Solde envoyé' : 'Solde reçu'}
                    {m.description ? ` · ${m.description}` : ''}
                  </div>
                  <div className="text-[11px] text-foreground-400">{formatDateTime(m.created_at)}</div>
                </div>
                <div className={`text-sm font-bold flex-shrink-0 ${isOut ? 'text-secondary-700' : 'text-accent-600'}`}>
                  {isOut ? '−' : '+'}
                  {formatMoney(Math.abs(Number(m.amount || 0)), currency)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}